import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { AccessTokenDto, Role } from "../services/auth/auth.dto";
import { useToast } from "./toast";

interface User {
  id: string;
  email: string;
  role: Role;
  username: string;
  firstName: "";
  lastName: "";
  permissions: string[];
  exp: Date;
  access_token: string;
  refresh_token: string;
  request_osbuk_user: boolean;
}

interface AuthContextModel {
  getUser: () => User;
  setToken: (input: AccessTokenDto, remember: boolean) => void;
  removeUserFromStorage: () => void;
}

let AuthContext = React.createContext<AuthContextModel>(null!);

const defaultUser: User = {
  id: "",
  email: "",
  role: Role.USER,
  username: "",
  firstName: "",
  lastName: "",
  permissions: [],
  exp: new Date(),
  access_token: "",
  refresh_token: "",
  request_osbuk_user: false
};

const getStoredUser = (): User => {
  const access_token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token") || "";
  const refresh_token = localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token") || "";

  if (!access_token) {
    return { ...defaultUser };
  }

  try {
    const decoded = jwtDecode(access_token) as any;
    const roleStr = decoded["role"];
    let role = Role.USER;
    if (roleStr === "admin") {
      role = Role.ADMIN;
    } else if (roleStr === "osbuk_user") {
      role = Role.OSBUK_USER;
    }

    return {
      id: decoded["id"] || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] || "",
      email: decoded["email"] || "",
      role: role,
      username: decoded["username"] || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] || "",
      firstName: decoded["first_name"] || "",
      lastName: decoded["last_name"] || "",
      permissions: [],
      exp: new Date((decoded["exp"] || 0) * 1000),
      access_token: access_token,
      refresh_token: refresh_token,
      request_osbuk_user: Boolean(decoded["request_osbuk_user"])
    };
  } catch {
    return { ...defaultUser };
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User>(getStoredUser);

  React.useEffect(() => {
    if (user.access_token) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + user.access_token;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, [user.access_token]);

  const removeUserFromStorage = React.useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    setUser(getStoredUser());
  }, []);

  const setToken = React.useCallback((input: AccessTokenDto, remember: boolean) => {
    if (remember) {
      if (input.access_token) {
        localStorage.setItem("access_token", input.access_token);
      }
      if (input.refresh_token) {
        localStorage.setItem("refresh_token", input.refresh_token);
      }
    } else {
      if (input.access_token) {
        sessionStorage.setItem("access_token", input.access_token);
      }
      if (input.refresh_token) {
        sessionStorage.setItem("refresh_token", input.refresh_token);
      }
    }
    setUser(getStoredUser());
  }, []);

  const getUser = React.useCallback(() => {
    return user;
  }, [user]);

  const value = React.useMemo(
    () => ({ getUser, setToken, removeUserFromStorage }),
    [getUser, setToken, removeUserFromStorage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

export function HasPermission({ role, children }: { role: Role; children: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();
  var user = auth.getUser();
  const toast = useToast();

  if (user.access_token.length === 0) {
    toast.show("Öncelikle giriş yapmalısınız.", "error");
    return <Navigate to="/loginsebis" state={{ from: location }} replace />;
  }
  if (user.role === Role.ADMIN || user.role === role || (role === Role.USER && user.role === Role.OSBUK_USER)) {
    return children;
  } else {
    toast.show("Bu sayfa için erişim yetkiniz yok.", "error");
    return <Navigate to="/access-denied" state={{ from: location.pathname }} replace />;
  }
}

// Yeni component: Kayıt olmayan kullanıcılar da sayfayı görebilir, ancak etkileşimli işlemler için login gerekir
export function PublicPageWrapper({ 
  children, 
  requireLoginForAction = false,
  actionMessage = "Bu işlemi gerçekleştirmek için giriş yapmalısınız."
}: { 
  children: JSX.Element; 
  requireLoginForAction?: boolean;
  actionMessage?: string;
}) {
  let auth = useAuth();
  let location = useLocation();
  var user = auth.getUser();
  const toast = useToast();

  // Eğer kullanıcı giriş yapmışsa, sayfayı göster
  if (user.access_token.length > 0) {
    return children;
  }

  // Eğer etkileşimli işlem gerekiyorsa ve kullanıcı giriş yapmamışsa
  if (requireLoginForAction) {
    toast.show(actionMessage, "error");
    return <Navigate to="/loginsebis" state={{ from: location }} replace />;
  }

  // Kayıt olmayan kullanıcılar sayfayı görebilir
  return children;
}
